import React, { useEffect, useState, useCallback } from 'react';
import { Calendar } from 'primereact/calendar';
import moment from 'moment-timezone';

const TimezoneDatePicker = ({ value, onChange, timezone, ...rest }) => {
  const [localValue, setLocalValue] = useState(null);

  /**
   * Convert UTC date to local timezone representation
   * Creates a new Date object with the same wall-clock time as the timezone
   */
  const toLocal = useCallback(
    (utcValue) => {
      if (!utcValue) return null;
      const m = moment(utcValue).tz(timezone);
      return new Date(m.year(), m.month(), m.date(), m.hour(), m.minute(), m.second(), m.millisecond());
    },
    [timezone]
  );

  /**
   * Convert an array of UTC dates to local timezone representation
   */
  const toLocalRange = useCallback(
    (range) => {
      if (!Array.isArray(range)) return null;
      return range.map((d) => (d ? toLocal(d) : null));
    },
    [toLocal]
  );

  // Handle incoming value (UTC → timezone)
  useEffect(() => {
    if (!value) {
      setLocalValue(null);
      return;
    }
    if (Array.isArray(value)) {
      setLocalValue(toLocalRange(value));
    } else {
      setLocalValue(toLocal(value));
    }
  }, [value, timezone, toLocal, toLocalRange]);

  /**
   * Convert local date to UTC
   * Takes the wall-clock time and interprets it as being in the target timezone
   */
  const toUTC = useCallback(
    (local) => {
      if (!local) return null;
      return moment
        .tz(
          {
            year: local.getFullYear(),
            month: local.getMonth(),
            day: local.getDate(),
            hour: local.getHours(),
            minute: local.getMinutes(),
            second: local.getSeconds(),
            millisecond: local.getMilliseconds(),
          },
          timezone
        )
        .utc()
        .toDate();
    },
    [timezone]
  );

  /**
   * Convert an array of local dates to UTC
   */
  const toUTCRange = useCallback(
    (range) => {
      if (!Array.isArray(range)) return null;
      return range.map((d) => (d ? toUTC(d) : null));
    },
    [toUTC]
  );

  // When user selects date(s)
  const handleChange = useCallback(
    (e) => {
      const picked = e.value;
      if (!picked) {
        onChange(null);
        return;
      }
      if (Array.isArray(picked)) {
        onChange(toUTCRange(picked));
      } else {
        onChange(toUTC(picked));
      }
    },
    [onChange, toUTC, toUTCRange]
  );

  return <Calendar value={localValue} onChange={handleChange} {...rest} />;
};

export default TimezoneDatePicker;
